/**
 * Meeting Management — pure helper functions (null-safe, render-safe).
 * Mirrors ProjectManagement/utils/projectHelpers.ts.
 */
import type { MeetingViewModel } from '@actschurch/ccms-sdk';
import type { MeetingState } from './meetingConstants';

/** Parse a backend date — ISO string or a yyyyMMddHHmmss numeric stamp. */
export function parseBackendDate(dt: string | number | null | undefined): Date | null {
  if (dt == null) return null;
  if (typeof dt === 'number') {
    const str = String(dt);
    if (str.length >= 8) {
      const year = parseInt(str.substring(0, 4));
      const month = parseInt(str.substring(4, 6)) - 1;
      const day = parseInt(str.substring(6, 8));
      const hours = str.length >= 10 ? parseInt(str.substring(8, 10)) : 0;
      const mins = str.length >= 12 ? parseInt(str.substring(10, 12)) : 0;
      return new Date(year, month, day, hours, mins);
    }
    return null;
  }
  const d = new Date(dt);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Convert a JS Date to the backend's decimal yyyyMMddHHmmss numeric stamp.
 * Inverse of parseBackendDate() for the numeric branch. Returns null for nullish input.
 */
export function toBackendStamp(d: Date | null | undefined): number | null {
  if (d == null || Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const MM = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const HH = pad(d.getHours());
  const mm = pad(d.getMinutes());
  const ss = pad(d.getSeconds());
  return Number(`${yyyy}${MM}${dd}${HH}${mm}${ss}`);
}

export function formatDate(dt: string | number | null | undefined, fallback = '—'): string {
  const d = parseBackendDate(dt);
  if (!d) return fallback;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

export function formatTime(dt: string | number | null | undefined, fallback = '—'): string {
  const d = parseBackendDate(dt);
  if (!d) return fallback;
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function formatDateTime(dt: string | number | null | undefined, fallback = '—'): string {
  const d = parseBackendDate(dt);
  if (!d) return fallback;
  return `${formatDate(dt)} · ${formatTime(dt)}`;
}

/** "8 Jun, 09:00–10:30" style label for a meeting's time window. */
export function formatTimeRange(
  start: string | number | null | undefined,
  end: string | number | null | undefined
): string {
  const s = parseBackendDate(start);
  const e = parseBackendDate(end);
  if (!s) return '—';
  const datePart = formatDate(start);
  const startTime = formatTime(start);
  if (!e) return `${datePart}, ${startTime}`;
  return `${datePart}, ${startTime}–${formatTime(end)}`;
}

/** Duration label e.g. "1h 30m". */
export function durationLabel(
  start: string | number | null | undefined,
  end: string | number | null | undefined
): string {
  const s = parseBackendDate(start);
  const e = parseBackendDate(end);
  if (!s || !e) return '—';
  const mins = Math.max(0, Math.round((e.getTime() - s.getTime()) / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

/**
 * Derive the lifecycle state from a meeting's status name + time window.
 * A status whose name signals cancellation/completion wins; otherwise the
 * current time vs. start/end decides Upcoming / In Progress / Completed.
 */
export function computeMeetingState(m: MeetingViewModel): MeetingState {
  const status = (m.statusName ?? '').toLowerCase();
  if (status.includes('cancel')) return 'Cancelled';
  if (status.includes('complete')) return 'Completed';

  const now = Date.now();
  const start = parseBackendDate(m.startDate)?.getTime();
  const end = parseBackendDate(m.endDate)?.getTime();
  if (end != null && now > end) return 'Completed';
  if (start != null && end != null && now >= start && now <= end) return 'In Progress';
  if (start != null && now < start) return 'Upcoming';
  // Fallbacks for missing dates
  if (status.includes('progress')) return 'In Progress';
  return 'Upcoming';
}

export function isUpcoming(m: MeetingViewModel): boolean {
  const start = parseBackendDate(m.startDate)?.getTime();
  return start != null && start >= Date.now() && computeMeetingState(m) !== 'Cancelled';
}

export function isToday(m: MeetingViewModel): boolean {
  const d = parseBackendDate(m.startDate);
  if (!d) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

export function isThisWeek(m: MeetingViewModel): boolean {
  const d = parseBackendDate(m.startDate);
  if (!d) return false;
  const now = new Date();
  const start = new Date(now);
  start.setDate(now.getDate() - now.getDay());
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 7);
  return d >= start && d < end;
}

/** Organizer display name from the enriched view model fields. */
export function getOrganizerName(m: MeetingViewModel): string {
  const name = [m.organizerName, m.organizerSurname].filter(Boolean).join(' ').trim();
  return name || m.organizerEmail || m.organizerID || 'Unknown';
}
